import { createFileRoute, Link } from '@tanstack/react-router';
import { Hourglass, Plus, TabletSmartphone } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { DialogoNuevoEquipo } from '@/admin/DialogoNuevoEquipo';
import { DialogoRestablecer } from '@/admin/DialogosUsuario';
import {
  DialogoAprobar,
  esPendiente,
  TarjetaEquipo,
  textoPendientes,
  type AccionEquipo,
} from '@/admin/TarjetaEquipo';
import { callables, mensajeError } from '@/datos/acciones';
import { useDispositivos, useEquipos, useUsuarios } from '@/datos/consultas';
import { aMillis, type DispositivoDoc, type EquipoDoc } from '@/datos/tipos';
import { exigir } from '@/lib/guardias';
import { permisos } from '@/lib/sesion';
import { useAhora } from '@/lib/tiempo';
import { Encabezado } from '@/shell/Encabezado';
import { ErrorCarga, Esqueleto, Tarjeta, Vacio } from '@/ui/basicos';
import { Boton } from '@/ui/boton';
import { Confirmar } from '@/ui/confirmar';
import { Contenedor } from '@/ui/pagina';

export const Route = createFileRoute('/_app/admin/equipos')({
  beforeLoad: exigir(permisos.administrar),
  component: Pagina,
});

type Dialogo = AccionEquipo;

function Pagina() {
  const equipos = useEquipos();
  const dispositivos = useDispositivos();
  const usuarios = useUsuarios();
  const ahora = useAhora();
  const [ultimo, setUltimo] = useState<Dialogo | null>(null);
  const [abierto, setAbierto] = useState(false);
  const [n, setN] = useState(0);
  const abrir = (d: Dialogo) => {
    setN((x) => x + 1);
    setUltimo(d);
    setAbierto(true);
  };
  const cerrar = (v: boolean) => !v && setAbierto(false);
  const dialogo = abierto ? ultimo : null;

  const nombre = useMemo(
    () => new Map((usuarios.data ?? []).map((u) => [u.id, u.nombre])),
    [usuarios.data],
  );
  const conPin = useMemo(
    () =>
      (usuarios.data ?? [])
        .filter((u) => u.activo && u.tienePin)
        .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')),
    [usuarios.data],
  );
  /** Active and pending devices per shared account, most recently used first. */
  const porEquipo = useMemo(() => {
    const m = new Map<string, DispositivoDoc[]>();
    for (const d of dispositivos.data ?? []) {
      if (!(d.activo || esPendiente(d)) || !d.equipoId) continue;
      m.set(d.equipoId, [...(m.get(d.equipoId) ?? []), d]);
    }
    for (const l of m.values())
      l.sort(
        (a, b) =>
          (aMillis(b.ultimoUso) ?? aMillis(b.enroladoEn) ?? 0) -
          (aMillis(a.ultimoUso) ?? aMillis(a.enroladoEn) ?? 0),
      );
    return m;
  }, [dispositivos.data]);
  /** Pending tablets of active shared accounts (the ones an admin can act on). */
  const pendientes = useMemo(() => {
    const activos = new Set((equipos.data ?? []).filter((e) => e.activo).map((e) => e.id));
    return (dispositivos.data ?? []).filter(
      (d) => esPendiente(d) && d.equipoId && activos.has(d.equipoId),
    ).length;
  }, [dispositivos.data, equipos.data]);
  const lista = useMemo(
    () =>
      [...(equipos.data ?? [])].sort(
        (a, b) => Number(b.activo) - Number(a.activo) || a.nombre.localeCompare(b.nombre, 'es'),
      ),
    [equipos.data],
  );

  const cambiarEstado = async (e: EquipoDoc) => {
    try {
      await callables.actualizarEquipo({ uid: e.id, activo: !e.activo });
      toast.success(e.activo ? `${e.nombre} quedó desactivado` : `${e.nombre} está activo`, {
        description: e.activo
          ? 'Sus tablets ya no muestran nombres ni aceptan PIN, y se cerraron las sesiones abiertas en ellas.'
          : 'Vuelve a entrar con su email y contraseña en la tablet.',
      });
    } catch (err) {
      toast.error('No se pudo cambiar el equipo', { description: mensajeError(err) });
      throw err;
    }
  };

  const revocar = async (d: DispositivoDoc) => {
    try {
      await callables.revocarDispositivo({ dispositivoId: d.id });
      toast.success(esPendiente(d) ? 'Tablet rechazada' : 'Dispositivo revocado', {
        description: esPendiente(d)
          ? 'No quedó aprobada: no muestra nombres ni acepta PIN.'
          : 'Ya no muestra nombres ni acepta PIN. Las sesiones abiertas en él se cerraron.',
      });
    } catch (err) {
      toast.error('No se pudo revocar', { description: mensajeError(err) });
      throw err;
    }
  };

  const nuevo = (
    <Boton
      variante="primario"
      onClick={() => abrir({ tipo: 'nuevo' })}
      aria-label="Nuevo equipo compartido"
      className="max-sm:size-11 max-sm:px-0"
    >
      <Plus /> <span className="max-sm:hidden">Nuevo equipo compartido</span>
    </Boton>
  );

  const q = equipos.isError ? equipos : dispositivos.isError ? dispositivos : null;

  return (
    <>
      <Encabezado
        titulo="Equipos compartidos"
        ayuda="equipos"
        subtitulo={
          equipos.data ? `${equipos.data.filter((e) => e.activo).length} activos` : 'Cargando…'
        }
        acciones={nuevo}
        buscador={false}
      />
      <Contenedor>
        <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div className="flex min-w-0 flex-col gap-4">
            {!q && pendientes > 0 && (
              <div
                role="status"
                className="flex items-start gap-3 rounded-[12px] border border-ray-strong/40 bg-ray-soft px-4 py-3 text-ray-texto"
              >
                <Hourglass className="mt-0.5 size-5 flex-none" aria-hidden="true" />
                <p className="text-[15px] leading-[1.45]">
                  <strong>{textoPendientes(pendientes)}.</strong>{' '}
                  {pendientes === 1
                    ? 'Apruébala solo si es una tablet del taller; si no la reconoces, revócala.'
                    : 'Aprueba solo las tablets del taller y revoca las que no reconozcas.'}
                </p>
              </div>
            )}
            {q ? (
              <ErrorCarga error={q.error} />
            ) : !equipos.data || !dispositivos.data ? (
              <Tarjeta className="flex flex-col gap-3 p-5" aria-busy="true">
                {Array.from({ length: 2 }, (_, i) => (
                  <Esqueleto key={i} className="h-28" />
                ))}
              </Tarjeta>
            ) : lista.length === 0 ? (
              <Tarjeta>
                <Vacio
                  icono={<TabletSmartphone />}
                  titulo="Aún no hay equipos compartidos"
                  accion={nuevo}
                >
                  Crea uno por cada tablet del taller. Solo toma un minuto.
                </Vacio>
              </Tarjeta>
            ) : (
              lista.map((e) => (
                <TarjetaEquipo
                  key={e.id}
                  e={e}
                  dispositivos={porEquipo.get(e.id) ?? []}
                  nombre={nombre}
                  ahora={ahora}
                  onAccion={abrir}
                />
              ))
            )}
          </div>

          <aside className="flex flex-col gap-4">
            <Tarjeta className="flex flex-col gap-2.5 p-5">
              <h2 className="titulo text-[18px]">Cómo funciona</h2>
              <ol className="flex list-decimal flex-col gap-2 pl-5 text-[15px] leading-[1.45] text-text-long">
                <li>Creas el equipo con un email y una contraseña.</li>
                <li>En la tablet se entra una vez con esos datos. Queda enrolada.</li>
                <li>
                  Cada persona elige su nombre y pone su PIN. Todo lo que hace queda a su nombre.
                </li>
              </ol>
            </Tarjeta>
            <Tarjeta className="flex flex-col gap-3 p-5">
              <h2 className="titulo text-[18px]">Quién puede entrar</h2>
              <p className="text-[15px] text-text-long">
                Todas las personas activas del taller con PIN{' '}
                <strong>({usuarios.data ? conPin.length : '…'})</strong>:
              </p>
              {usuarios.data && conPin.length === 0 ? (
                <p className="text-[14px] text-text-2">
                  Nadie tiene PIN todavía: las tablets no mostrarán nombres.
                </p>
              ) : (
                <ul className="flex flex-wrap gap-2" aria-label="Personas con PIN">
                  {conPin.map((u) => (
                    <li
                      key={u.id}
                      className="inline-flex h-[26px] items-center rounded-full bg-surface-muted px-2.5 text-[13px] whitespace-nowrap text-text-long"
                    >
                      {u.nombre}
                    </li>
                  ))}
                </ul>
              )}
              <Link
                to="/admin/usuarios"
                className="text-[15px] font-bold text-primary-text underline-offset-4 hover:underline"
              >
                Administrar PIN en Usuarios
              </Link>
            </Tarjeta>
          </aside>
        </div>
      </Contenedor>

      <DialogoNuevoEquipo
        key={`nuevo-${ultimo?.tipo === 'nuevo' ? n : 0}`}
        abierto={dialogo?.tipo === 'nuevo'}
        onCambio={cerrar}
      />
      {ultimo?.tipo === 'clave' && (
        <DialogoRestablecer
          key={`clave-${n}`}
          uid={ultimo.e.id}
          nombre={ultimo.e.nombre}
          equipo
          abierto={dialogo?.tipo === 'clave'}
          onCambio={cerrar}
        />
      )}
      {ultimo?.tipo === 'estado' && (
        <Confirmar
          abierto={dialogo?.tipo === 'estado'}
          onCambio={cerrar}
          titulo={
            ultimo.e.activo ? `¿Desactivar ${ultimo.e.nombre}?` : `¿Reactivar ${ultimo.e.nombre}?`
          }
          textoConfirmar={ultimo.e.activo ? 'Desactivar' : 'Reactivar'}
          peligro={ultimo.e.activo}
          onConfirmar={() => cambiarEstado(ultimo.e)}
        >
          {ultimo.e.activo
            ? 'La cuenta deja de funcionar: sus tablets no muestran nombres ni aceptan PIN y se cierran las sesiones abiertas en ellas. Las inspecciones ya registradas se conservan.'
            : 'La cuenta vuelve a funcionar. Sus tablets siguen enroladas si no las revocaste.'}
        </Confirmar>
      )}
      {ultimo?.tipo === 'aprobar' && (
        <DialogoAprobar
          key={`aprobar-${n}`}
          d={ultimo.d}
          abierto={dialogo?.tipo === 'aprobar'}
          onCambio={cerrar}
        />
      )}
      {ultimo?.tipo === 'revocar' && (
        <Confirmar
          abierto={dialogo?.tipo === 'revocar'}
          onCambio={cerrar}
          titulo={`¿Revocar ${ultimo.d.modelo ?? ultimo.d.nombre}?`}
          textoConfirmar="Revocar"
          peligro
          onConfirmar={() => revocar(ultimo.d)}
        >
          {esPendiente(ultimo.d) ? (
            'La tablet no queda aprobada: no mostrará los nombres ni aceptará PIN. Si era del taller, entra de nuevo con el email del equipo y apruébala.'
          ) : (
            <>
              Deja de mostrar los nombres y no acepta más PIN.{' '}
              {ultimo.d.sesiones?.length
                ? `Se cierran las sesiones de ${ultimo.d.sesiones.length === 1 ? 'la persona que entró' : `las ${ultimo.d.sesiones.length} personas que entraron`} en ella. `
                : ''}
              Para volver a usarla, entra de nuevo con el email del equipo y se enrola otra vez.
            </>
          )}
        </Confirmar>
      )}
    </>
  );
}
